import { Form } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Customize from './Customize';
import Preview from './Preview';
import initialValues from './initialValues.json';
import { useDsRequestContext } from './context';
import DSRequestFormService from '../../services/DSRequestForm.service';
import useNotify from '../../context/Notification/useNotify';
import usePermission from '../../hooks/usePermission';
import classNames from 'classnames';

export default function PreviewForm({
    domainSelection,
    getCustomize,
    initialValue,
    requestType,
}) {
    const [loading, setLoading] = useState(false);

    const notify = useNotify();
    const { t } = useTranslation();

    const UIContext = useDsRequestContext();
    const UIProps = useMemo(() => {
        return {
            value: UIContext.value,
            setValue: UIContext.setValue,
            dsType: UIContext.dsType,
            previewLang: UIContext.previewLang,
            setPreviewLang: UIContext.setPreviewLang,
        };
    }, [UIContext]);
    const [dataSubjectTypes, setDataSubjectTypes] = useState([]);
    const [dataSubjectTypeAddOns, setDataSubjectTypeAddOn] = useState([]);
    const [policyAndTermsOfService, setPolicyAndTermsOfService] = useState();

    const [form] = Form.useForm();

    const customizeStyle = Form.useWatch('customizeStyle', form);
    const customizeForm = Form.useWatch('customizeForm', form);
    const fontStyle = Form.useWatch('fontStyle', form);
    const logo = Form.useWatch('logo', form);
    const thankyou = Form.useWatch('thankyou', form);
    const rightDescriptionList =
        Form.useWatch('rightDescriptionList', form) || [];

    useEffect(() => {
        if (customizeForm?.dataSubjectType) {
            setDataSubjectTypes(customizeForm?.dataSubjectType);
        }
        if (customizeForm?.dataSubjectTypeAddOn) {
            setDataSubjectTypeAddOn(customizeForm?.dataSubjectTypeAddOn);
        }
        if (customizeForm?.policyAndTermsOfService) {
            setPolicyAndTermsOfService(customizeForm?.policyAndTermsOfService);
        }
    }, [customizeForm]);

    const detailsOfClaim = form.getFieldValue([
        'customizeForm',
        'detailsOfClaim',
    ]);

    const allFalse =
        detailsOfClaim &&
        Object.values(detailsOfClaim).every((value) => value === false);

    const initialRightDescriptionList = useMemo(() => {
        return initialValue?.rightDescriptionList || [];
    }, [initialValue]);

    const values = useMemo(() => {
        return {
            ...initialValue,
            customizeStyle: {
                ...initialValue?.customizeStyle,
                ...customizeStyle,
            },
            customizeForm: {
                ...initialValue?.customizeForm,
                ...customizeForm,
            },
            fontStyle: {
                ...initialValue?.fontStyle,
                ...fontStyle,
            },
            logo: {
                ...initialValue?.logo,
                ...logo,
            },
            thankyou: {
                ...initialValue?.thankyou,
                ...thankyou,
            },
            rightDescriptionList: [
                ...initialRightDescriptionList,
                ...rightDescriptionList,
            ],
        };
    }, [
        initialValue,
        customizeStyle,
        customizeForm,
        fontStyle,
        logo,
        thankyou,
        initialRightDescriptionList,
        rightDescriptionList,
    ]);

    const onFinish = async () => {
        try {
            setLoading(true);
            const dataSubjectType = dataSubjectTypes?.map((data) => {
                const dsType = UIProps.dsType.find((ds) => ds.id === data);
                let id = '';
                if (dsType) id = dsType.id;
                return {
                    id,
                    isUsed: true,
                    name: dsType?.nameTh,
                    isAddOn: dsType?.isAddOn ?? false,
                };
            });
            const dataSubjectTypeAddOn = dataSubjectTypeAddOns?.map(
                (data) => data?.dataSubjectAddOnName
            );

            let payload = {
                ...values,
                logo: {
                    companyLogoId: form.getFieldValue([
                        'logo',
                        'companyLogoId',
                    ]),
                    hiddenLogo: form.getFieldValue(['logo', 'hiddenLogo']),
                },
            };

            payload.customizeForm = {
                ...payload.customizeForm,
                policyAndTermsOfService,
                dataSubjectType,
                dataSubjectTypeAddOn,
            };
            payload.customizeStyle = {
                ...payload.customizeStyle,
                buttonColor: payload.customizeStyle?.buttonColor,
            };

            if (payload?.updateByFullname) delete payload?.updateByFullname;
            if (payload?.updateDate) delete payload?.updateDate;
            if (payload?.requestTypeId) delete payload?.requestTypeId;

            if (!allFalse) {
                const { data } = await DSRequestFormService.editCustomize(
                    payload
                );
                if (data?.resultCode === 1) {
                    notify({
                        title: t('common.Success'),
                        description: t('Successfully save data'),
                        timeOut: 1800,
                    });
                    getCustomize();
                    setLoading(false);
                } else {
                    setLoading(false);
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('common.FailedSaveData'),
                    });
                }
            } else setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
            notify({
                type: 'error',
                title: t('common.Fail'),
                description: t('common.FailedSaveData'),
            });
        }
    };

    useEffect(() => {
        form.setFieldsValue({ ...initialValue });
        setPolicyAndTermsOfService(
            initialValue?.customizeForm?.policyAndTermsOfService
        );
    }, [initialValue]);

    const { isAuditor } = usePermission();

    return (
        <Form
            scrollToFirstError={{
                behavior: 'smooth',
                block: 'center',
            }}
            form={form}
            onFinish={onFinish}
            layout="vertical"
            initialValues={initialValues}
            className={classNames('grid gap-4', {
                'grid-cols-1': isAuditor,
                'grid-cols-4': !isAuditor,
            })}
        >
            <Preview
                form={form}
                loading={loading}
                domainSelection={domainSelection}
                values={values}
                lang={UIProps.previewLang}
                requestType={requestType}
            />
            {!isAuditor && (
                <Customize
                    initialValue={initialValue}
                    form={form}
                    requestType={requestType}
                    values={values}
                />
            )}
        </Form>
    );
}
